import { useState, useEffect, useCallback } from 'react';
import type { TaskListCatalog } from '@techgitter/contracts';
import {
  getTaskLists,
  refreshTaskListsCache,
  invalidateTaskListsCache,
} from '../services/taskListCacheService';

const EMPTY_CATALOG: TaskListCatalog = { schemaVersion: 1, taskLists: [] };

export function useTaskLists() {
  const [taskListCatalog, setTaskListCatalog] = useState<TaskListCatalog>(EMPTY_CATALOG);
  const [isLoading, setIsLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getTaskLists();
      if (signal?.aborted) return;
      setTaskListCatalog(result.data);
      setIsStale(result.isStale ?? false);
      if (result.warning) {
        console.warn('[useTaskLists]', result.warning);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (signal?.aborted) return;
      console.error('[useTaskLists] ERROR:', err.message);
      setError(err.message || 'Failed to load task lists');
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getTaskLists({ forceRefresh: true });
      setTaskListCatalog(result.data);
      setIsStale(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load task lists');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const result = await refreshTaskListsCache();
    if (result.success && result.data) {
      setTaskListCatalog(result.data);
      setIsStale(false);
      setError(null);
    } else if (result.error) {
      setError(result.error);
    }
    setIsLoading(false);
  }, []);

  const invalidate = useCallback(async () => {
    setIsLoading(true);
    const result = await invalidateTaskListsCache();
    setTaskListCatalog(EMPTY_CATALOG);
    setIsStale(false);
    setError(null);

    try {
      const fresh = await getTaskLists({ forceRefresh: true });
      setTaskListCatalog(fresh.data);
    } catch (err: any) {
      setError(err.message || 'Failed to reload task lists');
    } finally {
      setIsLoading(false);
    }

    return result;
  }, []);

  return { taskListCatalog, isLoading, isStale, error, retry, refresh, invalidate };
}
